import { ReactNode, useEffect } from "react";
import { Link } from "wouter";
import { ArrowRight, ChevronRight } from "lucide-react";
import { SearchStateLink, type SearchState } from "@/components/search/SearchStateLink";
import { applyPageMeta } from "@/lib/seo";
import { getPlatformConfig } from "@/lib/platform/config";
import { getCanonicalPublicOrigin } from "@/lib/canonical-origin";
import { useAppTranslation } from "@/i18n/useAppTranslation";
import type { FaqItem } from "@/content/geo";
import { canonicalCityGuidePath } from "../../../../../lib/copy/src/city-guide-policy.mjs";

function geoLinkHref(href: string) {
  const bareCity = href.match(/^\/cities\/([^/]+)$/);
  const cityPath = bareCity ? canonicalCityGuidePath(bareCity[1]) : null;
  const target = cityPath || href;
  return /^\/(?:uk|fr|it)\/cities\//.test(target) ? `~${target}` : target;
}

interface BreadcrumbEntry {
  label: string;
  href: string;
}

interface JsonLdInput {
  kind: "Article" | "Service";
  headline: string;
  description: string;
  url: string;
  image?: string;
  datePublished?: string;
  dateModified?: string;
  faqs: FaqItem[];
  breadcrumbs: BreadcrumbEntry[];
}

function buildJsonLd(input: JsonLdInput) {
  const config = getPlatformConfig();
  const siteUrl = getCanonicalPublicOrigin();
  // City guides emit a Service entity (we offer "classic car hire in <city>")
  // wrapped in a WebPage; model/use-case/comparison guides emit Article. This
  // matches the editorial contract documented in the GEO style guide.
  const primary: Record<string, unknown> =
    input.kind === "Service"
      ? {
          "@type": "Service",
          "@id": input.url,
          name: input.headline,
          description: input.description,
          url: input.url,
          serviceType: "Classic car hire",
          ...(input.image ? { image: input.image } : {}),
          provider: { "@type": "Organization", name: config.name, url: siteUrl },
          areaServed: input.headline,
        }
      : {
          "@type": "Article",
          "@id": input.url,
          headline: input.headline,
          description: input.description,
          url: input.url,
          ...(input.image ? { image: input.image } : {}),
          ...(input.datePublished ? { datePublished: input.datePublished } : {}),
          ...(input.dateModified ? { dateModified: input.dateModified } : {}),
          author: { "@type": "Organization", name: config.name, url: siteUrl },
          publisher: { "@type": "Organization", name: config.name, url: siteUrl },
          isPartOf: { "@type": "WebSite", name: config.name, url: siteUrl },
        };
  const graph: Record<string, unknown>[] = [
    primary,
    {
      "@type": "BreadcrumbList",
      itemListElement: input.breadcrumbs.map((b, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: b.label,
        item: b.href.startsWith("http") ? b.href : `${siteUrl}${b.href}`,
      })),
    },
  ];
  if (input.faqs.length > 0) {
    graph.push({
      "@type": "FAQPage",
      mainEntity: input.faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });
  }
  return { "@context": "https://schema.org", "@graph": graph };
}

interface GeoPageLayoutProps {
  title: string;
  tldr: string;
  hero: { image: string; alt: string };
  metaTitle: string;
  metaDescription: string;
  canonicalPath: string;
  breadcrumbs: BreadcrumbEntry[];
  jsonLdKind: "Article" | "Service";
  faqs: FaqItem[];
  updatedAt: string;
  children: ReactNode;
  footerCta?: { label: string; href: string; searchState?: SearchState };
  related?: ReactNode;
}

export function GeoPageLayout({
  title,
  tldr,
  hero,
  metaTitle,
  metaDescription,
  canonicalPath,
  breadcrumbs,
  jsonLdKind,
  faqs,
  updatedAt,
  children,
  footerCta,
  related,
}: GeoPageLayoutProps) {
  const { t } = useAppTranslation();
  useEffect(() => {
    // City guides already carry complete server metadata. Keep it for this
    // exact SSR document instead of appending a brand suffix during startup.
    const servedCanonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]')?.href;
    if (/^\/(?:[a-z]{2}\/)?cities\//.test(canonicalPath) &&
        document.getElementById("root")?.hasAttribute("data-prerender-root") &&
        servedCanonical && new URL(servedCanonical).pathname === canonicalPath &&
        window.location.pathname === canonicalPath) return;
    const config = getPlatformConfig();
    const url = `${getCanonicalPublicOrigin()}${canonicalPath}`;
    applyPageMeta({
      title: metaTitle,
      description: metaDescription,
      canonical: url,
      ...(/^\/(?:uk|fr|it)\/cities\//.test(canonicalPath) ? { hreflang: [] } : {}),
      ogType: jsonLdKind === "Article" ? "article" : "website",
      ogImage: hero.image,
      preloadImage: hero.image,
      jsonLd: buildJsonLd({
        kind: jsonLdKind,
        headline: title,
        description: metaDescription,
        url,
        image: hero.image,
        dateModified: updatedAt,
        faqs,
        breadcrumbs,
      }),
    });
  }, [
    canonicalPath,
    metaTitle,
    metaDescription,
    hero.image,
    jsonLdKind,
    title,
    updatedAt,
    faqs,
    breadcrumbs,
  ]);

  return (
    <article>
      <section className="relative flex min-h-[44vh] items-center justify-center overflow-hidden bg-foreground">
        <img
          src={hero.image}
          alt={hero.alt}
          className="absolute inset-0 h-full w-full object-cover"
          fetchPriority="high"
          decoding="async"
        />
        <div className="absolute inset-0 bg-black/60" />
        <div className="relative z-10 mx-auto max-w-4xl px-4 py-16 text-center">
          <nav aria-label={t("geo.breadcrumb")} className="mb-4 flex justify-center text-xs text-white/70">
            <ol className="flex flex-wrap items-center gap-1">
              {breadcrumbs.map((b, i) => (
                <li key={b.href} className="flex items-center gap-1">
                  {i > 0 && <ChevronRight className="h-3 w-3" />}
                  {i === breadcrumbs.length - 1 ? (
                    <span className="text-white/80">{b.label}</span>
                  ) : (
                    <Link href={geoLinkHref(b.href)} className="hover:underline">
                      {b.label}
                    </Link>
                  )}
                </li>
              ))}
            </ol>
          </nav>
          <h1 className="font-sans text-3xl font-bold leading-tight tracking-tight text-white sm:text-4xl md:text-5xl">
            {title}
          </h1>
          <p className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-white/85 sm:text-lg">
            {tldr}
          </p>
          {footerCta && (
            <div className="mt-7 flex justify-center">
              {footerCta.searchState ? (
                <SearchStateLink
                  searchPath={footerCta.href}
                  state={footerCta.searchState}
                  data-testid="link-geo-hero-search"
                  className="primary-action cursor-pointer inline-flex items-center gap-2 rounded-lg px-6 py-3 text-sm font-semibold transition-colors"
                >
                  {footerCta.label}
                  <ArrowRight className="h-4 w-4" />
                </SearchStateLink>
              ) : <Link
                href={footerCta.href}
              className="primary-action cursor-pointer inline-flex items-center gap-2 rounded-lg px-6 py-3 text-sm font-semibold transition-colors"
              >
                {footerCta.label}
                <ArrowRight className="h-4 w-4" />
              </Link>}
            </div>
          )}
        </div>
      </section>

      <section className="bg-background py-12 sm:py-16">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <div className="rounded-xl border border-border bg-muted/40 p-5 sm:p-6">
            <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
              {t("geo.tldr_label")}
            </p>
            <p className="mt-2 text-sm leading-relaxed text-foreground sm:text-base">{tldr}</p>
          </div>
        </div>
      </section>

      <div className="bg-background pb-12">{children}</div>

      {faqs.length > 0 && (
        <section className="bg-muted/30 py-16">
          <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
            <h2 className="font-sans text-2xl font-bold text-foreground sm:text-3xl">
              {t("geo.faq_heading")}
            </h2>
            <dl className="mt-6 divide-y divide-border">
              {faqs.map((f, i) => (
                <div key={i} className="py-5">
                  <dt className="text-base font-semibold text-foreground">{f.q}</dt>
                  <dd className="mt-2 text-sm leading-relaxed text-muted-foreground">{f.a}</dd>
                </div>
              ))}
            </dl>
          </div>
        </section>
      )}

      {related && (
        <section className="bg-background py-12">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">{related}</div>
        </section>
      )}

      <section className="bg-foreground py-12 text-background">
        <div className="mx-auto max-w-3xl px-4 text-center sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-white">{t("geo.find_your_classic")}</h2>
          <p className="mx-auto mt-3 max-w-xl text-sm text-white/80">
            {t("geo.cta_description")}
          </p>
          <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
            {footerCta?.searchState ? (
              <SearchStateLink
                searchPath={footerCta.href}
                state={footerCta.searchState}
                data-testid="link-geo-end-search"
                className="primary-action cursor-pointer inline-flex items-center gap-2 rounded-lg px-6 py-3 text-sm font-semibold"
              >
                {footerCta.label}
                <ArrowRight className="h-4 w-4" />
              </SearchStateLink>
            ) : <Link
              href={footerCta?.href || "/search"}
              className="primary-action cursor-pointer inline-flex items-center gap-2 rounded-lg px-6 py-3 text-sm font-semibold"
            >
              {footerCta?.label || t("geo.browse_cars")}
              <ArrowRight className="h-4 w-4" />
            </Link>}
            <Link
              href="/list-your-car"
              className="cursor-pointer inline-flex items-center gap-2 rounded-lg border border-white/30 bg-transparent px-6 py-3 text-sm font-semibold text-white hover:bg-white/10"
            >
              {t("geo.list_your_car")}
            </Link>
          </div>
          <p className="mt-6 text-xs text-white/50">
            {t("geo.last_reviewed", { date: updatedAt })}
          </p>
        </div>
      </section>
    </article>
  );
}

export function RelatedLinks({
  heading,
  links,
}: {
  heading: string;
  links: { label: string; href: string }[];
}) {
  if (links.length === 0) return null;
  return (
    <div>
      <h3 className="text-sm font-semibold uppercase tracking-widest text-muted-foreground">
        {heading}
      </h3>
      <ul className="mt-3 flex flex-wrap gap-2">
        {links.map((l) => (
          <li key={l.href}>
            <Link
              href={geoLinkHref(l.href)}
              className="cursor-pointer inline-flex items-center gap-1 rounded-full border border-border bg-background px-3 py-1.5 text-sm text-foreground hover:bg-muted"
            >
              {l.label}
              <ArrowRight className="h-3 w-3" />
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
